/**
 * StratIQX For Consultants Page
 * White-label strategic intelligence for consulting practices
 * Dedicated page for consultant audience
 */

import React from 'react'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../components/ui/Button'
import { Card, CardContent } from '../components/ui/Card'
import { MainNavigation } from '../components/navigation/MainNavigation'
import { MainFooter } from '../components/navigation/MainFooter'
import { trackPageView, trackCTAClick, initScrollDepthTracking } from '../utils/analytics'
import {
  Clock,
  TrendingUp,
  Award,
  Target,
  Zap,
  Rocket,
  CheckCircle,
  ArrowRight,
  Download,
  DollarSign,
  Users,
  BarChart3
} from 'lucide-react'

export const ForConsultants: React.FC = () => {
  const navigate = useNavigate()

  // Track page view and scroll depth on mount
  useEffect(() => {
    trackPageView('For Consultants', '/for-consultants')
    const cleanup = initScrollDepthTracking('For Consultants')
    return cleanup
  }, [])

  const handleGetStarted = () => {
    navigate('/onboarding-info')
  }

  const handleDownloadSample = () => {
    // TODO: Link to actual sample report PDF
    console.log('Download sample report')
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <MainNavigation variant="full" />

      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden">
          <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:32px_32px]" />

          <div className="container mx-auto max-w-4xl relative">
            <div className="max-w-4xl mx-auto text-center">
              {/* Trust Badge */}
              <div className="inline-flex items-center space-x-2 bg-blue-600/20 backdrop-blur-sm border border-blue-400/30 rounded-full px-4 py-2 mb-8">
                <Award className="h-4 w-4 text-blue-400" />
                <span className="text-sm text-blue-200">Trusted by 200+ Consulting Practices</span>
              </div>

              {/* Headline */}
              <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
                White-Label Strategic Intelligence
                <br />
                <span className="text-4xl md:text-5xl">for Consulting Practices</span>
              </h1>

              {/* Subheadline */}
              <p className="text-xl md:text-2xl text-gray-300 mb-4">
                Deliver <span className="text-blue-400 font-semibold">$50,000 engagements</span> with a{' '}
                <span className="text-cyan-400 font-semibold">$149 research foundation</span>
              </p>

              <p className="text-lg text-gray-400 mb-10 max-w-2xl mx-auto">
                Replace 20+ hours of desk research • 4-hour turnaround • 51+ premium sources
              </p>

              {/* CTA Button */}
              <div className="flex items-center justify-center mb-12">
                <Button
                  onClick={handleDownloadSample}
                  size="lg"
                  className="bg-white text-blue-900 hover:bg-gray-100 px-8 py-4 text-lg font-semibold"
                >
                  <Download className="h-5 w-5 mr-2" />
                  Download Sample Report
                </Button>
              </div>

              {/* Trust Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-6">
                  <Clock className="h-8 w-8 text-blue-400 mx-auto mb-3" />
                  <div className="text-3xl font-bold mb-1">20+ Hours</div>
                  <div className="text-sm text-gray-300">Saved Per Engagement</div>
                </div>

                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-6">
                  <TrendingUp className="h-8 w-8 text-cyan-400 mx-auto mb-3" />
                  <div className="text-3xl font-bold mb-1">95% Margin</div>
                  <div className="text-sm text-gray-300">Maintained</div>
                </div>

                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-6">
                  <Award className="h-8 w-8 text-green-400 mx-auto mb-3" />
                  <div className="text-3xl font-bold mb-1">100% Your</div>
                  <div className="text-sm text-gray-300">Brand</div>
                </div>
              </div>
            </div>
          </div>
      </section>

      {/* The Consultant's Dilemma */}
      <section className="py-20 px-4 bg-slate-800/30 border-y border-blue-800/30">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-white mb-4">
                The Consultant's Dilemma
              </h2>
              <p className="text-xl text-gray-300">
                Every engagement starts with expensive research
              </p>
            </div>

            {/* Comparison Cards */}
            <div className="grid md:grid-cols-2 gap-8">
              {/* Current State */}
              <Card className="border-2 border-red-400/30 bg-red-900/20 backdrop-blur-sm">
                <CardContent className="p-8">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold text-white">❌ Current State</h3>
                  </div>

                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between text-gray-300">
                      <span>40 hours desk research</span>
                      <span className="font-semibold">$6,000</span>
                    </div>
                    <div className="flex justify-between text-gray-300">
                      <span>Junior analyst @ $150/hr</span>
                      <span className="font-semibold">3 weeks</span>
                    </div>
                    <div className="flex justify-between text-gray-300">
                      <span>Client billing</span>
                      <span className="font-semibold text-green-400">$25,000</span>
                    </div>
                  </div>

                  <div className="border-t-2 border-red-400/30 pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-white">Your Margin:</span>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-white">$19,000</div>
                        <div className="text-sm text-gray-300">(76% margin)</div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* With StratIQX */}
              <Card className="border-2 border-green-400/50 bg-green-900/20 backdrop-blur-sm shadow-lg">
                <CardContent className="p-8">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold text-white">✅ With StratIQX</h3>
                  </div>

                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between text-gray-300">
                      <span>2 hours review</span>
                      <span className="font-semibold text-blue-400">$149</span>
                    </div>
                    <div className="flex justify-between text-gray-300">
                      <span>StratIQX foundation</span>
                      <span className="font-semibold text-blue-400">4 hours</span>
                    </div>
                    <div className="flex justify-between text-gray-300">
                      <span>Client billing</span>
                      <span className="font-semibold text-green-400">$25,000</span>
                    </div>
                  </div>

                  <div className="border-t-2 border-green-400 pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-white">Your Margin:</span>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-green-400">$24,851</div>
                        <div className="text-sm text-gray-300">(99% margin)</div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-green-600/20 rounded-lg border border-green-300">
                    <div className="text-center">
                      <div className="text-sm text-gray-300">Extra Profit Per Engagement</div>
                      <div className="text-xl font-bold text-green-400">+ $5,851</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* How Consultants Use StratIQX */}
      <section className="py-20 px-4 bg-gradient-to-b from-slate-900 to-slate-800">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-4">
                How Consultants Use StratIQX
              </h2>
              <p className="text-xl text-gray-300">
                Three proven ways to 10x your research capacity
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Card 1: Qualify */}
              <Card className="border-2 border-blue-500/30 bg-slate-800/80 backdrop-blur-sm hover:border-blue-400/50 transition-all hover:shadow-lg">
                <CardContent className="p-8">
                  <div className="bg-blue-600/20 rounded-full w-16 h-16 flex items-center justify-center mb-6">
                    <Target className="h-8 w-8 text-blue-400" />
                  </div>

                  <h3 className="text-2xl font-bold text-white mb-4">
                    1️⃣ Qualify Leads
                  </h3>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-start space-x-2">
                      <CheckCircle className="h-5 w-5 text-blue-400 mt-1 flex-shrink-0" />
                      <span className="text-gray-300">Buy $49 brief for prospects</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <CheckCircle className="h-5 w-5 text-blue-400 mt-1 flex-shrink-0" />
                      <span className="text-gray-300">Screen leads same day</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <CheckCircle className="h-5 w-5 text-blue-400 mt-1 flex-shrink-0" />
                      <span className="text-gray-300">Scope $50K engagement</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <CheckCircle className="h-5 w-5 text-blue-400 mt-1 flex-shrink-0" />
                      <span className="text-gray-300">Win with speed & depth</span>
                    </div>
                  </div>

                  <div className="bg-blue-600/10 rounded-lg p-4 border border-blue-500/30">
                    <div className="text-sm text-gray-400 mb-1">ROI Per Use</div>
                    <div className="text-2xl font-bold text-blue-400">$49 → $50K</div>
                  </div>
                </CardContent>
              </Card>

              {/* Card 2: Baseline */}
              <Card className="border-2 border-cyan-500/30 bg-slate-800/80 backdrop-blur-sm hover:border-cyan-400/50 transition-all hover:shadow-lg">
                <CardContent className="p-8">
                  <div className="bg-cyan-600/20 rounded-full w-16 h-16 flex items-center justify-center mb-6">
                    <Zap className="h-8 w-8 text-cyan-400" />
                  </div>

                  <h3 className="text-2xl font-bold text-white mb-4">
                    2️⃣ Baseline Research
                  </h3>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-start space-x-2">
                      <CheckCircle className="h-5 w-5 text-cyan-400 mt-1 flex-shrink-0" />
                      <span className="text-gray-300">Use $149 Market Essentials</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <CheckCircle className="h-5 w-5 text-cyan-400 mt-1 flex-shrink-0" />
                      <span className="text-gray-300">Start work immediately</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <CheckCircle className="h-5 w-5 text-cyan-400 mt-1 flex-shrink-0" />
                      <span className="text-gray-300">Add your expertise on top</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <CheckCircle className="h-5 w-5 text-cyan-400 mt-1 flex-shrink-0" />
                      <span className="text-gray-300">Bill full engagement value</span>
                    </div>
                  </div>

                  <div className="bg-cyan-600/10 rounded-lg p-4 border border-cyan-500/30">
                    <div className="text-sm text-gray-400 mb-1">Time Saved</div>
                    <div className="text-2xl font-bold text-cyan-400">38 hours</div>
                  </div>
                </CardContent>
              </Card>

              {/* Card 3: Accelerate */}
              <Card className="border-2 border-green-500/30 bg-slate-800/80 backdrop-blur-sm hover:border-green-400/50 transition-all hover:shadow-lg">
                <CardContent className="p-8">
                  <div className="bg-green-600/20 rounded-full w-16 h-16 flex items-center justify-center mb-6">
                    <Rocket className="h-8 w-8 text-green-400" />
                  </div>

                  <h3 className="text-2xl font-bold text-white mb-4">
                    3️⃣ Scale Practice
                  </h3>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-start space-x-2">
                      <CheckCircle className="h-5 w-5 text-green-400 mt-1 flex-shrink-0" />
                      <span className="text-gray-300">Replace 20hrs per project</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <CheckCircle className="h-5 w-5 text-green-400 mt-1 flex-shrink-0" />
                      <span className="text-gray-300">Focus on client strategy</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <CheckCircle className="h-5 w-5 text-green-400 mt-1 flex-shrink-0" />
                      <span className="text-gray-300">Close deals faster</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <CheckCircle className="h-5 w-5 text-green-400 mt-1 flex-shrink-0" />
                      <span className="text-gray-300">3x capacity without hiring</span>
                    </div>
                  </div>

                  <div className="bg-green-600/10 rounded-lg p-4 border border-green-500/30">
                    <div className="text-sm text-gray-400 mb-1">Margin Improvement</div>
                    <div className="text-2xl font-bold text-green-400">76% → 99%</div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing for Agencies */}
      <section className="py-20 px-4 bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-4">
                Pricing for Agencies
              </h2>
              <p className="text-xl text-gray-300">
                Start small, scale as you grow
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Pay-As-You-Go */}
              <Card className="border-2 border-slate-700 bg-slate-800/80 backdrop-blur-sm hover:border-blue-500/50 transition-all">
                <CardContent className="p-8">
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold text-white mb-2">Pay-As-You-Go</h3>
                    <p className="text-gray-400">Perfect for testing and occasional projects</p>
                  </div>

                  <div className="space-y-4 mb-8">
                    <div className="flex justify-between items-center pb-3 border-b border-slate-700">
                      <span className="text-gray-300">Board Executive Brief</span>
                      <span className="text-2xl font-bold text-blue-400">$49</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-slate-700">
                      <span className="text-gray-300">Market Essentials</span>
                      <span className="text-2xl font-bold text-blue-400">$149</span>
                    </div>
                  </div>

                  <div className="space-y-3 mb-8">
                    <div className="flex items-center space-x-2 text-gray-300">
                      <CheckCircle className="h-5 w-5 text-green-400" />
                      <span>No commitment required</span>
                    </div>
                    <div className="flex items-center space-x-2 text-gray-300">
                      <CheckCircle className="h-5 w-5 text-green-400" />
                      <span>Use only when you need</span>
                    </div>
                    <div className="flex items-center space-x-2 text-gray-300">
                      <CheckCircle className="h-5 w-5 text-green-400" />
                      <span>30-day money-back guarantee</span>
                    </div>
                  </div>

                  <Button
                    onClick={handleGetStarted}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3"
                  >
                    Get Started
                  </Button>
                </CardContent>
              </Card>

              {/* Volume Pricing */}
              <Card className="border-2 border-blue-500/50 bg-gradient-to-br from-blue-900/30 via-slate-800/80 to-slate-800/80 backdrop-blur-sm shadow-lg relative">
                <div className="absolute top-0 right-0 bg-blue-600 text-white px-4 py-1 rounded-bl-lg text-sm font-semibold">
                  BEST VALUE
                </div>

                <CardContent className="p-8">
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold text-white mb-2">Volume Pricing</h3>
                    <p className="text-gray-400">For active consulting practices</p>
                  </div>

                  <div className="space-y-4 mb-8">
                    <div className="flex justify-between items-center pb-3 border-b border-blue-500/30">
                      <span className="text-gray-300">10-24 reports/month</span>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-blue-400">$119</div>
                        <div className="text-sm text-gray-500">20% off</div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-blue-500/30">
                      <span className="text-gray-300">25-49 reports/month</span>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-blue-400">$104</div>
                        <div className="text-sm text-gray-500">30% off</div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-blue-500/30">
                      <span className="text-gray-300">50+ reports/month</span>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-blue-400">Custom</div>
                        <div className="text-sm text-gray-500">Contact us</div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-600/20 rounded-lg p-4 mb-6 border border-blue-500/30">
                    <div className="text-sm text-gray-300 mb-2">Annual Savings Example</div>
                    <div className="text-sm text-gray-400 mb-1">5 engagements/month @ $25K each</div>
                    <div className="text-2xl font-bold text-blue-400">Save $351,060/year</div>
                  </div>

                  <div className="text-center text-gray-400 py-3">
                    <p className="text-sm">Contact us for volume pricing details</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-slate-900 to-blue-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:32px_32px]" />

        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to 10x Your Research Capacity?
            </h2>

            <p className="text-xl text-gray-300 mb-10">
              Start with a free sample report to see the quality
            </p>

            <div className="flex items-center justify-center mb-8">
              <Button
                onClick={handleDownloadSample}
                size="lg"
                className="bg-white text-blue-900 hover:bg-gray-100 px-8 py-4 text-lg font-semibold"
              >
                <Download className="h-5 w-5 mr-2" />
                Download Sample Report
              </Button>
            </div>

            <div className="mb-8">
              <p className="text-gray-400 mb-2">Or jump right in:</p>
              <Button
                onClick={handleGetStarted}
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-12 py-4 text-xl font-bold"
              >
                Get Your First Report - $49
                <ArrowRight className="h-6 w-6 ml-2" />
              </Button>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-300">
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span>No commitment</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span>Instant delivery</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span>30-day guarantee</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Direct Access for SMBs Section */}
      <section className="py-16 bg-slate-800/50 border-t border-slate-700/50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="text-2xl font-bold text-white mb-4">
              Not a Consultant?
            </h3>
            <p className="text-lg text-gray-300 mb-6">
              We serve businesses directly too. Get the same consulting-grade strategic intelligence
              for your own organization.
            </p>
            <Button
              onClick={handleGetStarted}
              size="lg"
              variant="outline"
              className="border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white px-8 py-3 text-lg"
            >
              Get Intelligence Report
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      <MainFooter variant="full" />
    </div>
  )
}

export default ForConsultants
